import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type {
  PlanningItem,
  PlanningTodo,
  VendorCategory,
  CatalogItem,
  PlanningStatus,
  ChecklistItemStatus,
  CreatePlanningTodoInput,
  ExpenseItemWithRelations,
} from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { ItemAutosuggest } from './ItemAutosuggest';
import { RoomAutosuggest } from './RoomAutosuggest';
import { useUsers } from '../../hooks/useUsers';
import { useCreateSettingsCatalogItem } from '../../hooks/useSettings';
import { useCreateRoom, useRooms } from '../../hooks/useFloorPlans';
import { useExpenseItems } from '../../hooks/useExpenses';
import {
  useAddPlanningItem,
  useUpdatePlanningItem,
  useAddPlanningTodo,
  useUpdatePlanningTodo,
  useDeletePlanningTodo,
} from '../../hooks/usePlanning';

interface PlanningItemModalProps {
  open: boolean;
  onClose: () => void;
  /** Parent planning list id (needed for create). */
  listId: string;
  /** Existing item when editing; null when creating. */
  item: PlanningItem | null;
  /** Event that scopes the expense-line selector. */
  eventId: string;
  /** Event venue — its rooms populate the Room selector. */
  venueId?: string | null;
  /**
   * Pre-selected expense line for new rows (e.g. when the itemized list was
   * opened by zooming into an expense). Also seeds the category.
   */
  defaultExpenseItemId?: string | null;
  categories: VendorCategory[];
  catalogItems: CatalogItem[];
  statuses: PlanningStatus[];
}

interface TodoDraft {
  /** Existing todo id (when editing an item with persisted todos); null for new local drafts. */
  id: string | null;
  title: string;
  dueDate: string;
  status: ChecklistItemStatus;
  isDone: boolean;
  assignedToUserId: string;
}

function toTodoDraft(todo: PlanningTodo): TodoDraft {
  return {
    id: todo.id,
    title: todo.title,
    dueDate: todo.dueDate ? new Date(todo.dueDate).toISOString().slice(0, 10) : '',
    status: todo.status,
    isDone: todo.isDone,
    assignedToUserId: todo.assignedToUserId ?? '',
  };
}

export function expenseLineLabel(expense: ExpenseItemWithRelations): string {
  const name = expense.itemName ?? expense.vendorCategoryName ?? 'Expense';
  const vendor = expense.vendorName ? ` — ${expense.vendorName}` : '';
  const price = `$${expense.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `${name}${vendor} (${price})`;
}

export function PlanningItemModal({
  open,
  onClose,
  listId,
  item,
  eventId,
  venueId,
  defaultExpenseItemId,
  categories,
  catalogItems,
  statuses,
}: PlanningItemModalProps) {
  const isEdit = item !== null;
  const { data: users = [] } = useUsers({ isActive: true });
  const { data: rooms = [] } = useRooms(venueId ?? undefined, !!venueId);
  const { data: expenseItems = [] } = useExpenseItems({ eventId });

  const addItem = useAddPlanningItem();
  const updateItem = useUpdatePlanningItem();
  const addTodo = useAddPlanningTodo();
  const updateTodo = useUpdatePlanningTodo();
  const deleteTodo = useDeletePlanningTodo();
  const createCatalogItem = useCreateSettingsCatalogItem();
  const createRoom = useCreateRoom();

  const [vendorCategoryId, setVendorCategoryId] = useState(item?.vendorCategoryId ?? '');
  const [catalogItemId, setCatalogItemId] = useState(item?.catalogItemId ?? '');
  // Item auto-detects list-vs-free-text: the input shows the name and the id
  // is set only when an existing entry is chosen. New rows start empty — the
  // item must be typed or picked every time.
  const [itemName, setItemName] = useState(
    item ? (item.itemName ?? item.catalogItemName ?? '') : '',
  );
  const [quantity, setQuantity] = useState(String(item?.quantity ?? 1));
  const [roomId, setRoomId] = useState(item?.roomId ?? '');
  const [roomName, setRoomName] = useState(item?.roomName ?? '');
  // Once the user touches the room field we stop auto-defaulting it.
  const [roomTouched, setRoomTouched] = useState(false);
  const [expenseItemId, setExpenseItemId] = useState(
    item?.expenseItemId ?? (item ? '' : (defaultExpenseItemId ?? '')),
  );
  const [unitPrice, setUnitPrice] = useState(String(item?.unitPrice ?? 0));
  // New rows default to the "Searching" status when it exists.
  const [statusId, setStatusId] = useState(
    item?.statusId ?? statuses.find((s) => s.name.trim().toLowerCase() === 'searching')?.id ?? '',
  );
  const [notes, setNotes] = useState(item?.notes ?? '');
  const [todos, setTodos] = useState<TodoDraft[]>(() => (item?.todos ?? []).map(toTodoDraft));
  const [error, setError] = useState('');

  // Only active categories are selectable, sorted alphabetically by name.
  const sortedCategories = useMemo(
    () => categories.filter((c) => c.isActive).sort((a, b) => a.name.localeCompare(b.name)),
    [categories],
  );
  const filteredCatalogItems = useMemo(
    () =>
      catalogItems
        .filter((c) => c.vendorCategoryId === vendorCategoryId)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [catalogItems, vendorCategoryId],
  );
  // Rooms come from the event venue only.
  const venueRooms = useMemo(
    () => rooms.filter((r) => r.venueId === venueId).sort((a, b) => a.name.localeCompare(b.name)),
    [rooms, venueId],
  );

  // New rows seed the category from the (default or chosen) expense line.
  useEffect(() => {
    if (isEdit || vendorCategoryId || !expenseItemId) return;
    const expense = expenseItems.find((e) => e.id === expenseItemId);
    if (expense?.vendorCategoryId) setVendorCategoryId(expense.vendorCategoryId);
  }, [isEdit, vendorCategoryId, expenseItemId, expenseItems]);

  // A venue with a single room defaults new rows to it.
  useEffect(() => {
    if (isEdit || roomTouched || roomId || roomName) return;
    if (venueRooms.length === 1) {
      setRoomId(venueRooms[0].id);
      setRoomName(venueRooms[0].name);
    }
  }, [isEdit, roomTouched, roomId, roomName, venueRooms]);

  const qtyNum = parseFloat(quantity) || 0;
  const priceNum = parseFloat(unitPrice) || 0;
  const total = qtyNum * priceNum;

  // Every planning line itemizes an expense line — the link is mandatory.
  const isValid =
    vendorCategoryId !== '' && (!!catalogItemId || !!itemName.trim()) && expenseItemId !== '';

  // Free-text room names can be added to the venue on the fly (default 40x40 ft
  // footprint — editable later in the venue's floor-plan settings).
  const handleCreateRoom = useCallback(
    (name: string) => {
      if (!venueId) return;
      createRoom.mutate(
        { venueId, name, widthFt: 40, heightFt: 40 },
        {
          onSuccess: (room) => {
            setRoomName(room.name);
            setRoomId(room.id);
          },
          onError: () => setError('Failed to add room to the venue'),
        },
      );
    },
    [createRoom, venueId],
  );

  const handleCreateItem = useCallback(
    (name: string) => {
      if (!vendorCategoryId) return;
      createCatalogItem.mutate(
        { vendorCategoryId, name },
        {
          onSuccess: (catalogItem) => {
            setItemName(catalogItem.name);
            setCatalogItemId(catalogItem.id);
          },
          onError: () => setError('Failed to create catalog item'),
        },
      );
    },
    [createCatalogItem, vendorCategoryId],
  );

  const isPending = addItem.isPending || updateItem.isPending;

  // ─── Todo draft management ────────
  const addTodoDraft = useCallback(() => {
    setTodos((prev) => [
      ...prev,
      { id: null, title: '', dueDate: '', status: 'TODO', isDone: false, assignedToUserId: '' },
    ]);
  }, []);

  const updateTodoDraft = useCallback((index: number, patch: Partial<TodoDraft>) => {
    setTodos((prev) => prev.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  }, []);

  const removeTodoDraft = useCallback(
    async (index: number) => {
      const todo = todos[index];
      // For an existing (persisted) todo on an existing item, delete it immediately.
      if (isEdit && todo.id) {
        await deleteTodo.mutateAsync(todo.id);
      }
      setTodos((prev) => prev.filter((_, i) => i !== index));
    },
    [todos, isEdit, deleteTodo],
  );

  function buildTodoInput(t: TodoDraft): CreatePlanningTodoInput {
    return {
      title: t.title.trim(),
      status: t.status,
      isDone: t.isDone,
      ...(t.dueDate ? { dueDate: t.dueDate } : {}),
      ...(t.assignedToUserId ? { assignedToUserId: t.assignedToUserId } : {}),
    };
  }

  const handleSubmit = useCallback(async () => {
    setError('');
    // A chosen catalog item wins; otherwise the typed text is free text.
    const catalogVal = catalogItemId || null;
    const itemNameVal = catalogItemId ? undefined : itemName.trim() || undefined;

    try {
      // Rooms are stored by id only — a typed room name that was never picked
      // from the dropdown resolves to an existing venue room (case-insensitive)
      // or is created on the venue, so free text is never silently dropped.
      let roomIdVal = roomId || null;
      const typedRoom = roomName.trim();
      if (!roomIdVal && typedRoom && venueId) {
        const existing = venueRooms.find(
          (r) => r.name.trim().toLowerCase() === typedRoom.toLowerCase(),
        );
        if (existing) {
          roomIdVal = existing.id;
        } else {
          const created = await createRoom.mutateAsync({
            venueId,
            name: typedRoom,
            widthFt: 40,
            heightFt: 40,
          });
          roomIdVal = created.id;
        }
      }

      if (isEdit && item) {
        await updateItem.mutateAsync({
          itemId: item.id,
          updates: {
            vendorCategoryId: vendorCategoryId || null,
            catalogItemId: catalogVal,
            itemName: itemNameVal ?? null,
            quantity: qtyNum,
            roomId: roomIdVal,
            expenseItemId,
            unitPrice: priceNum,
            statusId: statusId || null,
            notes: notes.trim() || null,
          },
        });
        // Sync todos: new drafts (id === null) get created.
        for (const t of todos) {
          if (!t.title.trim()) continue;
          if (t.id === null) {
            await addTodo.mutateAsync({ itemId: item.id, ...buildTodoInput(t) });
          } else {
            await updateTodo.mutateAsync({ todoId: t.id, updates: buildTodoInput(t) });
          }
        }
      } else {
        const todoInputs = todos.filter((t) => t.title.trim()).map(buildTodoInput);
        await addItem.mutateAsync({
          listId,
          vendorCategoryId: vendorCategoryId || null,
          catalogItemId: catalogVal,
          itemName: itemNameVal,
          quantity: qtyNum,
          roomId: roomIdVal,
          expenseItemId,
          unitPrice: priceNum,
          statusId: statusId || null,
          notes: notes.trim() || undefined,
          ...(todoInputs.length > 0 ? { todos: todoInputs } : {}),
        });
      }
      onClose();
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to save planning item';
      setError(message);
    }
  }, [
    vendorCategoryId,
    catalogItemId,
    itemName,
    qtyNum,
    roomId,
    roomName,
    venueId,
    venueRooms,
    expenseItemId,
    priceNum,
    statusId,
    notes,
    todos,
    isEdit,
    item,
    listId,
    createRoom,
    updateItem,
    addItem,
    addTodo,
    updateTodo,
    onClose,
  ]);

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Row' : 'Add Row'}
      size="lg"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            data-testid="planning-item-submit"
          >
            {isPending ? 'Saving…' : isEdit ? 'Save' : 'Add Row'}
          </button>
        </div>
      }
    >
      {error && (
        <div className="mb-4 px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>
      )}

      <div className="space-y-4">
        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
          <SearchableListSelect
            items={sortedCategories}
            value={vendorCategoryId}
            onChange={(v) => {
              setVendorCategoryId(v as string);
              setCatalogItemId('');
            }}
            getValue={(c) => c.id}
            getLabel={(c) => c.name}
            placeholder="Select category..."
            ariaLabel="Category"
          />
        </div>

        {/* Item — search the catalog or type a new one (auto-detected) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Item *</label>
          <ItemAutosuggest
            value={itemName}
            items={filteredCatalogItems}
            disabled={!vendorCategoryId}
            onChange={(name, id) => {
              setItemName(name);
              setCatalogItemId(id ?? '');
            }}
            onCreateItem={handleCreateItem}
          />
        </div>

        {/* Expense line — mandatory; every planning line itemizes an expense */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Expense Line *</label>
          <SearchableListSelect
            items={expenseItems}
            value={expenseItemId}
            onChange={(v) => setExpenseItemId(v as string)}
            getValue={(e) => e.id}
            getLabel={(e) => expenseLineLabel(e)}
            placeholder="Select expense line..."
            ariaLabel="Expense line"
          />
          {expenseItems.length === 0 && (
            <p className="mt-1 text-xs text-amber-600">
              No expenses yet for this event — add an expense first.
            </p>
          )}
        </div>

        {/* Room — pick from the venue's rooms or type a new one to add it */}
        {venueId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Room</label>
            <RoomAutosuggest
              value={roomName}
              rooms={venueRooms}
              onChange={(name, id) => {
                setRoomTouched(true);
                setRoomName(name);
                setRoomId(id ?? '');
              }}
              onCreateRoom={handleCreateRoom}
            />
          </div>
        )}

        {/* Quantity + Unit Price + Total */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
            <input
              type="number"
              value={quantity}
              min="0"
              step="1"
              onChange={(e) => setQuantity(e.target.value)}
              aria-label="Quantity"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
            <input
              type="number"
              value={unitPrice}
              min="0"
              step="0.01"
              onChange={(e) => setUnitPrice(e.target.value)}
              aria-label="Unit price"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total</label>
            <div
              data-testid="planning-item-total"
              className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-sm text-gray-700"
            >
              $
              {total.toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <SearchableListSelect
            items={statuses}
            value={statusId}
            onChange={(v) => setStatusId(v as string)}
            getValue={(s) => s.id}
            getLabel={(s) => s.name}
            placeholder="Select status..."
            ariaLabel="Status"
            renderItem={(s, isSelected) => (
              <div className={`flex items-center gap-2 ${isSelected ? 'font-medium' : ''}`}>
                <span
                  className="w-3 h-3 rounded-full inline-block border border-gray-200"
                  style={{ backgroundColor: s.color ?? '#e5e7eb' }}
                />
                {s.name}
              </div>
            )}
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            aria-label="Notes"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>

        {/* Todos */}
        <div className="border-t border-gray-100 pt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-800">Todos</h4>
            <button
              type="button"
              onClick={addTodoDraft}
              className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700"
              data-testid="add-todo"
            >
              <Plus size={14} /> Add Todo
            </button>
          </div>
          {todos.length === 0 ? (
            <p className="text-xs text-gray-400">No todos yet.</p>
          ) : (
            <div className="space-y-2">
              {todos.map((t, i) => (
                <div
                  key={t.id ?? `draft-${i}`}
                  className="flex flex-wrap items-center gap-2 border border-gray-100 rounded-lg p-2"
                  data-testid="todo-row"
                >
                  <input
                    type="checkbox"
                    checked={t.isDone}
                    onChange={(e) =>
                      updateTodoDraft(i, {
                        isDone: e.target.checked,
                        status: e.target.checked ? 'DONE' : 'TODO',
                      })
                    }
                    aria-label="Todo done"
                  />
                  <input
                    type="text"
                    value={t.title}
                    onChange={(e) => updateTodoDraft(i, { title: e.target.value })}
                    placeholder="Todo title"
                    aria-label="Todo title"
                    className="flex-1 min-w-[120px] px-2 py-1 border border-gray-200 rounded text-sm"
                  />
                  <input
                    type="date"
                    value={t.dueDate}
                    onChange={(e) => updateTodoDraft(i, { dueDate: e.target.value })}
                    aria-label="Todo due date"
                    className="px-2 py-1 border border-gray-200 rounded text-sm"
                  />
                  <select
                    value={t.assignedToUserId}
                    onChange={(e) => updateTodoDraft(i, { assignedToUserId: e.target.value })}
                    aria-label="Todo assignee"
                    className="px-2 py-1 border border-gray-200 rounded text-sm"
                  >
                    <option value="">Unassigned</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => removeTodoDraft(i)}
                    className="text-gray-400 hover:text-red-600"
                    aria-label="Remove todo"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </EntityModal>
  );
}
